import { Paper, Button, MenuItem, Select, FormControl, InputLabel } from "@mui/material";
import { useEffect, useState } from "react";
import axios from "axios";
import Model from '../Model.js';
import FinancialStepper from './FinancialStepper.js';
import FinancialPicker from "./FinancialPicker.js";
import { DatePicker } from "@mui/x-date-pickers";
import dayjs from "dayjs";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { Alert, Snackbar } from '@mui/material';


function Financials(props) {
    const defaultFinancial = {...Model};
    const [financials, setFinancials] = useState([{}]);
    const [selectedFinancial, setSelectedFinancial] = useState(defaultFinancial);
    const [displayStepper, setDisplayStepper] = useState(false);
    const [selectedFid, setSelectedFid] = useState(0);
    const [divisions, setDivisions] = useState([]);
    const [selectedDivision, setSelectedDivision] = useState('');
    const [filteredFinancials, setFilteredFinancials] = useState([{}]);
    const [peers, setPeers] = useState([{}]);
    const [uploadError, setUploadError] = useState('');
    const [showError, setShowError] = useState(false);
    const [uploadSuccess, setUploadSuccess] = useState('');
    const [showSuccess, setShowSuccess] = useState(false);
    const [isUploading, setIsUploading] = useState(false);

    // Function to generate dynamic header text
    const getHeaderText = () => {
        if (!displayStepper) {
            return "Financial Reporting";
        }
        
        if (selectedFinancial.isYTD) {
            if (selectedFinancial.fiscalStart) {
                const year = new Date(selectedFinancial.fiscalStart).getFullYear();
                return `Financial Reporting - YTD Through Selected Period (${year})`;
            }
            return "Financial Reporting - YTD Through Selected Period";
        } else {
            if (selectedFinancial.fiscalEnd) {
                const year = new Date(selectedFinancial.fiscalEnd).getFullYear();
                return `Financial Reporting - Full Fiscal ${year}`;
            }
            return "Financial Reporting - Full Fiscal";
        }
    };


    const refreshFinancials = () => {
        axios({
            // Endpoint to send files
            url: process.env.REACT_APP_API_URL + 'FiscalInput',
            method: "Get",
            data: {},
        }).then((res) => {
            setFinancials(res.data);
            // Extract unique divisions from the data
            const uniqueDivisions = [...new Set(res.data.map(item => item.division).filter(Boolean))];
            setDivisions(uniqueDivisions);
            // Initialize with all financials
            setFilteredFinancials(res.data);
        }).catch((err) => {
            console.log(err)
        });
    }

    const refreshPeers = () => {
        axios({
            url: process.env.REACT_APP_API_URL + 'PeerGroup',
            method: "Get",
            data: {},
        }).then((res) => {
            // Extract unique divisions from the data
            setPeers(res.data);
        }).catch((err) => {
            console.log(err)
        });
    }


    //Load up the financials for the users company
    useEffect(() => {
        refreshFinancials();
        refreshPeers();
    }, []);

    // Update filtered financials when division selection changes
    useEffect(() => {
        if (selectedDivision === '') {
            setFilteredFinancials(financials);
        } else {
            setFilteredFinancials(financials.filter(f => f.division === selectedDivision));
        }
    }, [selectedDivision, financials]);

    const handleDivisionChange = (e) => {
        setSelectedDivision(e.target.value);
        setSelectedFid(0); // Reset financial selection when division changes
    }

    const enterNew = () => {
        // Set the new financial with the selected division and peer group ID
        const selectedPeerGroup = peers.find(pg => pg.division === selectedDivision);
        const newFinancial = {
            ...Model, 
            division: selectedDivision,
            peerGroupId: selectedPeerGroup ? selectedPeerGroup.peerGroupId : 0
        };
        setSelectedFinancial(newFinancial);
        setDisplayStepper(true);
    }

    const submitSelectedFinancial = (e) => {
        console.log('Submitting financial with peerGroupId:', selectedFinancial.peerGroupId);
        console.log('Full financial data:', selectedFinancial);

        // Set loading state
        setIsUploading(true);
        setUploadError('');
        setShowError(false);
        setUploadSuccess('');
        setShowSuccess(false);

        axios({
            // Endpoint to send files
            url: process.env.REACT_APP_API_URL + 'FiscalInput',
            method: selectedFinancial.fiscalInputId === 0 ? "POST" : "PUT",
            data: { ...selectedFinancial },
        }).then((res) => {
            // Clear loading state
            setIsUploading(false);
            
            // Check if response status is in 2xx range
            if (res.status >= 200 && res.status < 300) {
                // Success - show success message
                setUploadSuccess('Fiscal report uploaded successfully!');
                setShowSuccess(true);
                
                // Wait a moment to show success message, then proceed
                setTimeout(() => {
                    setSelectedFinancial(defaultFinancial);
                    setSelectedFid(0);
                    setDisplayStepper(false);
                    refreshFinancials();
                    setShowSuccess(false);
                }, 2000);
            } else {
                // Non-2xx response - treat as error
                let errorMessage = 'Unexpected response from server. Please try again.';
                if (res.data && res.data.message) {
                    errorMessage = res.data.message;
                }
                
                setUploadError(errorMessage);
                setShowError(true);
                
                // Don't change screens - keep user on current form
            }
        }).catch((err) => {
            // Clear loading state
            setIsUploading(false);
            
            console.log(err);
            
            // Set error message and show error notification
            let errorMessage = 'Failed to upload fiscal report. Please try again.';
            
            if (err.response) {
                // Server responded with error status
                if (err.response.status === 400) {
                    errorMessage = 'Invalid data provided. Please check your input and try again.';
                } else if (err.response.status === 401) {
                    errorMessage = 'Authentication failed. Please log in again.';
                } else if (err.response.status === 403) {
                    errorMessage = 'You do not have permission to submit this report.';
                } else if (err.response.status === 500) {
                    errorMessage = 'Server error occurred. Please try again later.';
                } else if (err.response.data && err.response.data.message) {
                    errorMessage = err.response.data.message;
                } else {
                    errorMessage = `Server returned status ${err.response.status}. Please try again.`;
                }
            } else if (err.request) {
                // Network error
                errorMessage = 'Network error. Please check your connection and try again.';
            }
            
            setUploadError(errorMessage);
            setShowError(true);
            
            // Don't change screens - keep user on current form
            // The stepper will remain active so user can fix issues and retry
        });
    }

    const handleCloseError = () => {
        setShowError(false);
    };

    const handleCloseSuccess = () => {
        setShowSuccess(false);
    };

    return (
        <div className="container">

            <h4>{getHeaderText()}</h4>
            
            {/* Error notification */}
            <Snackbar 
                open={showError} 
                autoHideDuration={6000} 
                onClose={handleCloseError}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ 
                    '& .MuiSnackbarContent-root': { 
                        minWidth: '600px',
                        maxWidth: '800px'
                    }
                }}
            >
                <Alert 
                    onClose={handleCloseError} 
                    severity="error" 
                    sx={{ 
                        width: '100%',
                        fontSize: '1.2rem',
                        padding: '16px 20px',
                        '& .MuiAlert-message': {
                            fontSize: '1.2rem',
                            fontWeight: '500'
                        }
                    }}
                    action={
                        <Button 
                            color="inherit" 
                            size="large" 
                            onClick={() => {
                                setShowError(false);
                                submitSelectedFinancial();
                            }}
                            sx={{ 
                                fontSize: '1.1rem',
                                padding: '8px 16px',
                                minWidth: '80px'
                            }}
                        >
                            Retry
                        </Button>
                    }
                >
                    {uploadError}
                </Alert>
            </Snackbar>

            {/* Success notification */}
            <Snackbar 
                open={showSuccess} 
                autoHideDuration={6000} 
                onClose={handleCloseSuccess}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ 
                    '& .MuiSnackbarContent-root': { 
                        minWidth: '600px',
                        maxWidth: '800px'
                    }
                }}
            >
                <Alert 
                    onClose={handleCloseSuccess} 
                    severity="success" 
                    sx={{ 
                        width: '100%',
                        fontSize: '1.2rem',
                        padding: '16px 20px',
                        '& .MuiAlert-message': {
                            fontSize: '1.2rem',
                            fontWeight: '500'
                        }
                    }}
                >
                    {uploadSuccess}
                </Alert>
            </Snackbar>

            {/* Loading notification */}
            <Snackbar 
                open={isUploading} 
                anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
                sx={{ 
                    '& .MuiSnackbarContent-root': { 
                        minWidth: '500px',
                        maxWidth: '600px'
                    }
                }}
            >
                <Alert 
                    severity="info" 
                    sx={{ 
                        width: '100%',
                        fontSize: '1.1rem',
                        padding: '16px 20px',
                        '& .MuiAlert-message': {
                            fontSize: '1.1rem',
                            fontWeight: '500'
                        }
                    }}
                >
                    Uploading fiscal report...
                </Alert>
            </Snackbar>
            
            <div className='componentContainer'>
                <div className='stepContainer'>
                    {!displayStepper ?
                        //Select an input to edit or enter new
                        <FinancialPicker 
                            displayStepper={displayStepper} 
                            setDisplayStepper={setDisplayStepper} 
                            defaultFinancial={defaultFinancial}
                            financials={filteredFinancials} 
                            selectedFid={selectedFid} 
                            setSelectedFid={setSelectedFid} 
                            selectedFinancial={selectedFinancial} 
                            setSelectedFinancial={setSelectedFinancial} 
                            allowNew={true}
                            onEnterNew={enterNew}
                            divisions={divisions}
                            selectedDivision={selectedDivision}
                            setSelectedDivision={setSelectedDivision}
                            peers={peers}
                        >
                        </FinancialPicker>
                    :
                        <FinancialStepper selectedFinancial={selectedFinancial} setSelectedFinancial={setSelectedFinancial} className='stepper' submitValues={submitSelectedFinancial} peerGroups={peers}></FinancialStepper>
                    }
                </div>
            </div>
        </div>
    );
}

export default Financials;